/**
 * ReferFriendModal Component
 * Modal for clients to refer friends and earn rewards
 */
import React, { useState } from 'react';
import { FaTimes, FaUserPlus, FaGift, FaSpinner, FaCheck, FaPhone, FaUser, FaBriefcase } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferFriendModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    friend_name: '',
    friend_profile: '',
    friend_contact: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.friend_name.trim() || !formData.friend_contact.trim()) {
      setError('Please fill in the name and contact number');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/client/referrals`, formData, { headers: headers() });
      setSuccess(true);
      setFormData({ friend_name: '', friend_profile: '', friend_contact: '' });
      
      setTimeout(() => {
        setSuccess(false);
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit referral');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-gradient-to-b from-[#1A2942] to-[#0F172A] rounded-2xl border border-[#2D5A8F] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 px-6 py-5 border-b border-[#2D5A8F]">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center shadow-lg">
              <FaUserPlus size={24} className="text-[#0A1929]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Refer a Friend</h2>
              <p className="text-gray-300 text-sm">Help your friends secure their financial future</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#0A1929]/50 flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-500/20 transition-all"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Reward Banner */}
        <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
          <div className="flex items-center space-x-3">
            <FaGift className="text-green-400 text-2xl" />
            <div>
              <p className="text-green-400 font-bold">Earn ₹300 per successful referral!</p>
              <p className="text-gray-400 text-xs">Your friend gets expert financial planning, you get rewarded.</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-500/20 rounded-xl border border-green-500/30 flex items-center space-x-3">
            <FaCheck className="text-green-400 text-xl" />
            <div>
              <p className="text-green-400 font-bold">Referral Submitted Successfully!</p>
              <p className="text-gray-400 text-sm">Your advisor will contact your friend soon.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-500/20 rounded-lg border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Friend's Name */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                <FaUser className="inline mr-2" />
                Friend's Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="friend_name"
                value={formData.friend_name}
                onChange={handleChange}
                placeholder="Enter your friend's full name"
                className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-all"
                required
              />
            </div>

            {/* Friend's Profile/Occupation */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                <FaBriefcase className="inline mr-2" />
                Profile / Occupation
              </label>
              <input
                type="text"
                name="friend_profile"
                value={formData.friend_profile}
                onChange={handleChange}
                placeholder="e.g., Business Owner, IT Professional, Doctor"
                className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-all"
              />
            </div>

            {/* Friend's Contact */}
            <div>
              <label className="block text-gray-400 text-sm mb-2">
                <FaPhone className="inline mr-2" />
                Contact Number <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                name="friend_contact"
                value={formData.friend_contact}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD700] transition-all"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0A1929] font-bold rounded-xl hover:shadow-lg hover:shadow-[#FFD700]/30 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {submitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <FaUserPlus />
                  <span>Submit Referral</span>
                </>
              )}
            </button>

            <p className="text-gray-500 text-xs text-center">
              By submitting, you confirm that you have your friend's permission to share their contact details.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReferFriendModal;
